import React, { useRef } from "react";
import img from "../../../constant";
import { X } from "lucide-react";

interface DPAContentProps {
  clientName: string;
  representativeName: string;
  representativeRole: string;
  date: string;
  effectiveDate: string;
  uploadedSignature?: File | null;
  signaturePreview?: string | null;
  onFileUpload?: (file: File) => void;
  onRemoveSignature?: () => void;
  readOnly?: boolean;
}

const DPAContent: React.FC<DPAContentProps> = ({
  clientName,
  date,
  effectiveDate,
  onFileUpload,
  onRemoveSignature,
  readOnly = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!validTypes.includes(file.type)) {
        alert("Please upload a valid image file (JPG, JPEG, or PNG)");
        return;
      }
      onFileUpload?.(file);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemoveSignature?.();
  };

  return (
    <div className="font-abel text-gray-700 text-sm leading-relaxed space-y-6 p-5 bg-white shadow-sm rounded-lg border border-gray-100 max-w-[1024px] mx-auto">
      {/* Header */}
      <div className="text-center mb-10 border-b pb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Data Processing Addendum
        </h1>
        <h2 className="text-xl font-semibold text-gray-600 mb-2">(DPA)</h2>
        <p className="text-base mt-4">
          {/* This Data Processing Addendum (DPA) is made effective as of{" "}
          <span className="font-semibold text-primary">{effectiveDate}</span>{" "}
          and forms part of the Master Services Agreement (MSA) between: */}
          This DPA is made effective as of the same date as the MSA and forms 
          part of the Master Services Agreement between the Parties.
        </p>
        <div className="mt-4 space-y-2 text-left max-w-md mx-auto bg-gray-50 rounded-lg p-4 text-sm">
          {/* <p>
            <strong>Provider:</strong> 25th &amp; Staffing Limited
          </p> */}
          <p className="mt-2">
            <strong>Customer:</strong>{" "}
            <span className="text-primary font-bold">{clientName}</span>
          </p>
        </div>
      </div>

      {/* Section 1 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          1. Definitions
        </h3>
        <ul className="space-y-2 list-disc pl-5">
          <li>
            Data Protection Laws mmeans all applicable data privacy and protection 
            legislation, including the Nigeria Data Protection Act 2023 (NDPA), 
            the EU General Data Protection Regulation 2016/679 (GDPR) where applicable, 
            and any implementing regulations or successor legislation
          </li>
          <li>
            Personal Data, Controller, Processor, Data Subject, Personal Data
            Breach, Processing, and Supervisory Authority have ascribed to 
            them under applicable Data Protection Laws
          </li>
          <li>
            Customer Data means all data provided by Customer to Provider 
            in connection with the Services, including Personal Data
          </li>
        </ul>
      </section>

      {/* Section 2 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          2. Roles and Processing Details
        </h3>
        <div className="space-y-3">
          <div>
            {/* <h4 className="font-semibold mb-1">2.1. Roles</h4> */}
            <p>
              Customer acts as Controller and Provider acts as 
              Processor of Personal Data processed in connection with the Services.
            </p>

            <div className="overflow-x-auto mt-2">
            <table className="min-w-full text-left text-xs border">
              <thead className="bg-gray-100 font-bold">
                <tr>
                  <th className="p-2 border">Processing Detail</th>
                  <th className="p-2 border">Particulars</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-2 border font-semibold">
                    Subject Matter
                  </td>
                  <td className="p-2 border">
                    Recruitment, hiring, onboarding, performance management, disciplinary processes, and offboarding
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border font-semibold">
                    Duration
                  </td>
                  <td className="p-2 border">
                    For the term of the MSA plus any period required for data return or deletion following termination
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border font-semibold">
                    Nature & Purpose
                  </td>
                  <td className="p-2 border">
                    Provision of the 25th Suite Services, including processing CVs, candidate profiles, interview data, employee records, 
                    leave, performance reviews, disciplinary logs, and salary/offer workflows
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border font-semibold">
                    Personal Data Types
                  </td>
                  <td className="p-2 border">
                    Name, contact details, employment history, education, skills assessments, interview recordings, 
                    financial details (salary, bank details, BVN, NIN), and other data in candidate/employee records
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border font-semibold">
                    Data Subjects
                  </td>
                  <td className="p-2 border">
                    Job applicants, candidates, current and former employees, and managers of Customer
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          </div>
          {/* <div>
            <h4 className="font-semibold mb-1">2.2. Details of Processing</h4>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <strong>Subject Matter:</strong> Processing of Personal Data
                related to Customer&apos;s recruitment, hiring, onboarding,
                performance management, disciplinary, and offboarding activities
              </li>
              <li>
                <strong>Duration:</strong> For the term of the MSA, plus any
                period required for data return/deletion post-termination
              </li>
              <li>
                <strong>Nature and Purpose:</strong> To provide the 25th Suite
                Services, including processing resumes, candidate profiles,
                interview data, employee records, leave requests, performance
                reviews, disciplinary logs, and salary/offer workflows
              </li>
              <li>
                <strong>Types of Personal Data:</strong> Name, contact details,
                employment history, education, skills, interview recordings,
                financial details (e.g., salary, bank details, BVN, NIN), and
                other data in resumes/profiles/records
              </li>
              <li>
                <strong>Categories of Data Subjects:</strong> Job applicants,
                candidates, employees, former employees, and managers of
                Customer
              </li>
            </ul>
          </div> */}
        </div>
      </section>

      {/* Section 3 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          3. Processor Obligations
        </h3>
        <p className="mb-2">Provider, as Processor, shall:</p>
        <ul className="space-y-2">
          <li>
            <strong>3.1.</strong> Process Personal Data only on documented instructions 
            from Customer, unless required to do so by applicable law
          </li>
          <li>
            <strong>3.2.</strong> Implement appropriate technical and 
            organisational security measures as set out in Annex 1
          </li>
          <li>
            <strong>3.3.</strong> Ensure that all personnel with access 
            to Personal Data are subject to binding confidentiality obligations
          </li>
          <li>
            <strong>3.4.</strong> Assist Customer (at Customer's reasonable cost) 
            in responding to Data Subject rights requests and in conducting Data 
            Protection Impact Assessments
          </li>
          <li>
            <strong>3.5.</strong> Notify Customer without undue delay and in any 
            event within 72 hours of becoming aware of a Personal Data Breach
          </li>
          <li>
            <strong>3.6.</strong> Upon termination of the MSA or written request 
            from Customer, delete or return all Personal Data in accordance with 
            the Data Retention Schedule (Annex 3)
          </li>
          <li>
            <strong>3.7.</strong> Allow audits by Customer or an independent auditor, 
            at Customer's cost, upon reasonable written notice (no more than once per calendar year)
          </li>
        </ul>
      </section>

      {/* Section 4 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          4.  Sub-Processors
        </h3>
        <p>Provider may engage Sub-Processors to assist in providing the Services, provided that: </p>
        <ul className="space-y-2 list-disc pl-5">
          <li>
            Provider notifies Customer in advance and gives Customer 30 days to raise a written objection
          </li>
          <li>
            If Customer's objection is reasonable and cannot be resolved, Customer may terminate the 
            affected Services without penalty
          </li>
          <li>
            Provider imposes data protection obligations on Sub-Processors equivalent to those in this DPA       
          </li>
          <li>
            Provider remains fully liable for the acts and omissions of its Sub-Processors. 
            Current authorised Sub-Processors are listed in Annex 2
          </li>
        </ul>
      </section>

      {/* Section 5 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          5. International Data Transfers
        </h3>
        <p>
          Customer acknowledges that the provision of the Services may involve transfers of 
          Personal Data to jurisdictions outside Nigeria (including AWS cloud regions and 
          Deepgram in the United States). Provider shall ensure that all such transfers are 
          made in compliance with applicable Data Protection Laws, including by implementing 
          Standard Contractual Clauses (SCCs) or equivalent safeguards as recognised under the 
          NDPA 2023. A copy of the applicable SCCs is available upon written request. Provider 
          shall not transfer Personal Data to any new jurisdiction without giving Customer at 
          least 30 days' prior written notice.
        </p>
      </section>

      {/* Section 6 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          6. Aggregated Data
        </h3>
        <p>
          Provider may use anonymised and aggregated data derived from Customer's use of the 
          Services for the purpose of improving and developing the Services, provided that such 
          data cannot reasonably be used to identify any individual or Customer. Customer may opt 
          out by providing written notice to Provider.
        </p>
      </section>

      {/* Section 7 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          7. Customer Obligations as Controller
        </h3>
        <p className="mb-2">Customer shall:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Ensure that all processing of Personal Data has a valid lawful basis under applicable Data Protection Laws</li>
          <li>
            Ensure that Data Subjects have received appropriate privacy notices
          </li>
          <li>Promptly respond to Data Subject requests and notify Provider of relevant instructions</li>
          <li>Maintain all required records of processing activities</li>
        </ul>
      </section>

      {/* Section 8 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          8. Liability and Indemnification
        </h3>
        <p>
          Each Party shall indemnify and hold harmless the other from and against all losses, 
          damages, fines, penalties, and costs arising from that Party's failure to comply with 
          its obligations under this DPA or applicable Data Protection Laws.
        </p>
      </section>

      {/* Section 9 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          9. Governing Law
        </h3>
        <p>
          This DPA is governed by the laws of the Federal Republic of Nigeria. 
          Any amendments must be made in writing and signed by authorised 
          representatives of both Parties.
        </p>
      </section>

      {/* Section 10 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          10. Termination
        </h3>
        <p>
          Upon expiry or termination of the MSA, Provider shall cease all processing of 
          Personal Data and shall, within 30 days of termination, either return or securely 
          delete all Personal Data in accordance with the Data Retention Schedule (Annex 3), 
          unless a longer retention period is required by applicable law.
        </p>
      </section>

      {/* Signatures */}
      {/* <div className="mt-10 border-t pt-8">
        <h3 className="font-bold text-xl mb-4">Execution</h3>
        <p className="text-sm text-gray-600 mb-6">
          This Agreement is executed by the undersigned, who represent that they
          are duly authorized to bind their respective Party.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div className="border p-6 rounded-lg bg-gray-50">
            <p className="font-bold mb-1">For Provider:</p>
            <p className="text-primary font-semibold mb-4">
              25th &amp; Staffing
            </p>
            <div className="space-y-3">
              <img src={img.atsuSign} alt="CTO Sign" className="h-16" />
              <p className="border-b border-gray-400 pb-1">
                <strong>Name:</strong> Atsu Emmanuel
              </p>
              <p className="border-b border-gray-400 pb-1">
                <strong>Title:</strong> CTO
              </p>
              <p className="border-b border-gray-400 pb-1">
                <strong>Date:</strong> {date}
              </p>
            </div>
          </div>

          <div className="border p-6 rounded-lg bg-gray-50">
            <p className="font-bold mb-1">For Customer:</p>
            <p className="text-primary font-semibold mb-4">{clientName}</p>
            <div className="space-y-3">
              <div className="h-16 w-full flex items-end">
                {signaturePreview ? (
                  <div
                    className={`relative group ${!readOnly ? "cursor-pointer" : ""}`}
                    onClick={!readOnly ? handleImageClick : undefined}
                  >
                    <img
                      src={signaturePreview}
                      alt="Uploaded Signature"
                      className="h-16 object-contain"
                    />
                    {!readOnly && (
                      <button
                        onClick={handleRemoveClick}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1
                          opacity-0 group-hover:opacity-100 transition-opacity duration-200
                          hover:bg-red-600"
                        aria-label="Remove signature"
                      >
                        <X size={16} />
                      </button>
                    )}
                    {!readOnly && (
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    )}
                  </div>
                ) : readOnly ? (
                  <div className="h-16 flex items-end">
                    <span className="border-b border-gray-400 block w-48 text-gray-400 text-xs pb-1">
                      Signature
                    </span>
                  </div>
                ) : (
                  <div className="hidden">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload Signature
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleFileChange}
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-md file:border-0
                        file:text-sm file:font-semibold
                        file:bg-primary file:text-white
                        hover:file:bg-primary/90
                        cursor-pointer"
                    />
                  </div>
                )}
              </div>
              <p className="border-b border-gray-400 pb-1">
                <strong>Name:</strong> {representativeName}
              </p>
              <p className="border-b border-gray-400 pb-1">
                <strong>Title:</strong>{" "}
                {representativeRole || "Authorized Representative"}
              </p>
              <p className="border-b border-gray-400 pb-1">
                <strong>Date:</strong> {date}
              </p>
            </div>
          </div>
        </div>
      </div> */}

      {/* Annexes */}
      <section className="mt-10 border-t pt-8">
        <h3 className="font-bold text-lg mb-3 text-gray-900">
          Annex 1: Security Measures
        </h3>

         <div className="overflow-x-auto mt-4">
          <table className="min-w-full divide-y divide-gray-200 border rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Security Domain
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Measures Implemented
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 text-sm">
              <tr>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                  Encryption
                </td>
                <td className="px-6 py-4">Data at rest: AES-256. Data in transit: TLS 1.3 or higher. 
                  Encryption keys managed and rotated on a defined schedule.
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                  Access Controls
                </td>
                <td className="px-6 py-4">Role-based access control (RBAC), multi-factor 
                  authentication (MFA) enforced for all administrative access, IP whitelisting, 
                  principle of least privilege applied to all system access.
                </td>
                
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                  Monitoring & Logging
                </td>
                <td className="px-6 py-4">Continuous security event monitoring, centralised 
                  log management, real-time alerting on anomalous activity, intrusion 
                  detection systems (IDS).
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                  Vulnerability Management
                </td>
                <td className="px-6 py-4">
                  Annual third-party penetration testing, quarterly internal vulnerability 
                  scans, patch management process with SLA-based remediation timelines.
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                  Physical Security
                </td>
                <td className="px-6 py-4">
                  Services hosted in ISO 27001-certified AWS data centres with physical 
                  access controls, CCTV, and environmental controls.
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                  Incident Response
                </td>
                <td className="px-6 py-4">
                  Documented incident response plan, 24/7 security operations monitoring, 
                  72-hour breach notification process, post-incident review procedures.
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                  Personnel Security
                </td>
                <td className="px-6 py-4">
                  Background checks for personnel with access to Personal Data, mandatory 
                  annual security awareness training, binding confidentiality obligations.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
      </section>

      <section className="mt-8">
        <h3 className="font-bold text-lg mb-3 text-gray-900">
          Annex 2: Authorized Sub-Processors
        </h3>

        <div className="overflow-x-auto mt-4">
          <table className="min-w-full divide-y divide-gray-200 border rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sub-Processor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Purpose
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data Categories
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 text-sm">
              <tr>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                  Amazon Web Services
                </td>
                <td className="px-6 py-4">Cloud hosting, storage, and compute</td>
                <td className="px-6 py-4">AWS regions</td>
                <td className="px-6 py-4">All categories of Personal Data</td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                  Deepgram
                </td>
                <td className="px-6 py-4">Audio and video transcription</td>
                <td className="px-6 py-4">United States</td>
                <td className="px-6 py-4">Interview recordings</td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                  Twilio/Termii
                </td>
                <td className="px-6 py-4">SMS and notification delivery</td>
                <td className="px-6 py-4">US / Nigeria</td>
                <td className="px-6 py-4">Phone numbers and messages</td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                  Google Workspace
                </td>
                <td className="px-6 py-4">
                  Internal Provider collaboration
                </td>
                <td className="px-6 py-4">Global</td>
                <td className="px-6 py-4">Limited metadata only</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="mt-4 text-sm text-gray-600">
          Provider shall notify Customer at least 30 days in advance of any proposed 
          addition or replacement of Sub-Processors. Customer may object in writing 
          within that period.
          <br />
          <br />
          {/* Customer may object in writing; if objection is reasonable, Provider
          will either not use the Sub-Processor or allow Customer to terminate
          without penalty. */}
        </p>
      </section>

      <section className="mt-8">
        <h3 className="font-bold text-lg mb-3 text-gray-900">
          Annex 3: Data Retention Schedule
        </h3>
        <p>This schedule sets out the retention periods applicable to each category of 
          Personal Data. Customer may request early deletion by written notice; 
          Provider shall complete such deletion within 10 business days.
        </p>

        <div className="overflow-x-auto mt-4">
          <table className="min-w-full divide-y divide-gray-200 border rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Retention (Active)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Post-Termination
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deletion Method
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 text-sm">
              <tr>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                  Candidate profiles and CVs
                </td>
                <td className="px-6 py-4">Duration of subscription</td>
                <td className="px-6 py-4">30 days then deleted</td>
                <td className="px-6 py-4">Secure erasure</td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                  Interview recordings
                </td>
                <td className="px-6 py-4">Per tier storage limits</td>
                <td className="px-6 py-4">30 days then deleted</td>
                <td className="px-6 py-4">Secure erasure</td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                  Employee records (Lifecycle)
                </td>
                <td className="px-6 py-4">Duration of subscription</td>
                <td className="px-6 py-4">90 days — deleted or exported</td>
                <td className="px-6 py-4">Secure erasure / export</td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                  Salary and financial data (BVN/NIN)
                </td>
                <td className="px-6 py-4">
                  Duration of subscription
                </td>
                <td className="px-6 py-4">30 days then deleted</td>
                <td className="px-6 py-4">Secure erasure</td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                  Performance and disciplinary records
                </td>
                <td className="px-6 py-4">
                  Duration of subscription
                </td>
                <td className="px-6 py-4">90 days — deleted or exported</td>
                <td className="px-6 py-4">Secure erasure / export</td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                  Audit and access logs
                </td>
                <td className="px-6 py-4">
                  Per tier (30/180/365 days)
                </td>
                <td className="px-6 py-4">End of retention period</td>
                <td className="px-6 py-4">Automated deletion</td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                  System and platform logs
                </td>
                <td className="px-6 py-4">
                  90 days rolling
                </td>
                <td className="px-6 py-4">Deleted on termination</td>
                <td className="px-6 py-4">Automated deletion</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default DPAContent;
